import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import SectionService from "../../services/Department/SectionService";
import TermService from "../../services/Department/TermService";
import styles from './Section.module.css';
import DepartmentBanner from "../common/DepartmentBanner";
import { FaSearch, FaSpinner, FaTable, FaTh, FaSort, FaSortUp, FaSortDown, FaMapMarkerAlt, FaUserTie, FaCalendarAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Pagination from '../common/Pagination';
import DepartmentService from "../../services/Department/DepartmentService";


const Section = () => {
  document.title = "Sections";
  const params = new URLSearchParams(window.location.search);


  const [isLoading, setIsLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [terms, setTerms] = useState([]);
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [disabledColumns, setDisabledColumns] = useState([]);
  const itemsPerPage = 20;

  // Define column types
  const COLUMN_TYPES = Object.freeze({
    SECTION_TITLE: 1,
    COURSE_NUMBER: 2,
    SCHEDULE_ID: 3,
    LOCATION: 4,
    INSTRUCTOR: 5
  });

  // Define valid sort keys as a frozen object for immutability
  const SORT_KEYS = Object.freeze({
    SECTION_TITLE: 'sectionTitle',
    COURSE_NUMBER: 'courseNumber',
    SCHEDULE_ID: 'scheduleId',
    LOCATION: 'location',
    INSTRUCTOR: 'instructorFullName',
  });
   
  useEffect(() => {
    
      // Get column configuration
      DepartmentService.GetColumnConfig(3)
        .then(response => {
          const { serviceDisabledItems } = response.data || response;
          if (serviceDisabledItems && serviceDisabledItems.trim() !== "") {
            setDisabledColumns(serviceDisabledItems.split(',').map(item => parseInt(item.trim())));
          } else {
            setDisabledColumns([]);
          }
        })
        .catch(error => {
          console.error('Error fetching column configuration:', error);
          setDisabledColumns([]);
        });

      setIsLoadingTerms(true);
      TermService.GetByDepartmentId()
        .then(response => {
          const terms = response.data || response;
          // Sort terms by academic year and term type
          terms.sort((a, b) => {
            if (b.academicYearId !== a.academicYearId) {
              return b.academicYearId - a.academicYearId;
            }
            return b.termType - a.termType;
          });
          setTerms(terms);
          
          // Get current term values from the first object
          const currentCalenderYear = terms[0]?.currentCalenderYear;
          const currentSemester = terms[0]?.currentSemester;
          
          // Set current term
          const currentTerm = terms.find(term => 
            term.academicYearName === currentCalenderYear && 
            term.termTypeStr === currentSemester
          );
          
          if (currentTerm) {
            setSelectedTerm(currentTerm.id);
          } else if (terms.length > 0) {
            setSelectedTerm(terms[0].id);
          }
        })
        .catch(error => {
          console.error('Error fetching terms:', error);
          setTerms([]);
        })
        .finally(() => {
          setIsLoadingTerms(false);
        });
    
  }, []);

  // Check if a column is enabled
  const isColumnEnabled = (columnType) => {
    return !disabledColumns.includes(columnType);
  };

  useEffect(() => {
    if (selectedTerm) {
      setIsLoading(true);
    SectionService.GetByDepartmentAndTerm(selectedTerm)
      .then(response => {
          setSections(Array.isArray(response.data) ? response.data : []);
      })
      .catch(error => {
          console.error('Error fetching sections:', error);
          setSections([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedTerm]);

  const filteredSections = sections.filter(section => {
    const searchLower = searchTerm.toLowerCase();
    return (
      section.sectionTitle?.toLowerCase().includes(searchLower) ||
      section.courseNumber?.toLowerCase().includes(searchLower) ||
      section.scheduleId?.toLowerCase().includes(searchLower) ||
      section.location?.toLowerCase().includes(searchLower) ||
      section.instructorFullName?.toLowerCase().includes(searchLower) ||
      section.crNstr?.toLowerCase().includes(searchLower)
    );
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedSections = (sections) => {
    if (!sortConfig.key) return sections;

    return [...sections].sort((a, b) => {
      const aValue = (a[sortConfig.key] || '').toLowerCase();
      const bValue = (b[sortConfig.key] || '').toLowerCase();
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" />
      : <FaSortDown className="ms-1" />;
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = getSortedSections(filteredSections).slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container-fluid p-0" 
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* <DepartmentBanner /> */}
        
        <div className="container-custom py-4">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb bg-transparent p-0">
              <li className="breadcrumb-item">
                {/* <a href={`./home?deptId=${deptId}`} className="text-decoration-none text-primary">
                  Home
                </a> */}
              </li>
              {/* <li className="breadcrumb-item active" aria-current="page">Section</li> */}
            </ol>
          </nav>

          {/* Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className={`card border-0 shadow-sm ${styles.statsCard}`}>
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Sections</p>
                  <p className="mb-0 h3">{sections.length}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className={`card border-0 shadow-sm ${styles.statsCard}`}>
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Unique Locations</p>
                  <p className="mb-0 h3">
                    {new Set(sections.map(section => section.location)).size}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className={`card border-0 shadow-sm ${styles.statsCard}`}>
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Instructors</p>
                  <p className="mb-0 h3">
                    {new Set(sections.map(section => section.instructorFullName)).size}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-3">
                  <p className="mb-0 h3">Section List</p>
                  <p className="text-muted mb-0">Browse available sections</p>
                </div>
                <div className="col-lg-9">
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="flex-grow-1 position-relative">
                      <FaSearch className={styles.searchIcon} />
                      <input
                        type="text"
                        className={`form-control ${styles.searchInput}`}
                        placeholder="Search sections..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    {isLoadingTerms ? (
                      <div className="d-flex align-items-center">
                        <FaSpinner className="spinner-border text-primary" style={{ width: '1rem', height: '1rem' }} />
                        <span className="ms-2">Loading terms...</span>
                      </div>
                    ) : (
                      <select 
                      aria-label="Select Term"
                        className="form-select" 
                        style={{ width: 'auto', borderRadius: '0', height: '38px', borderColor: '#dee2e6' }}
                        value={selectedTerm || ''}
                        onChange={(e) => setSelectedTerm(e.target.value)}
                        disabled={isLoadingTerms || terms.length === 0}
                      >
                        {terms.length > 0 ? (
                          terms.map(term => (
                            <option key={term.id} value={term.id}>
                              {term.termAcademicYear}
                            </option>
                          ))
                        ) : (
                          <option value="">No terms available</option>
                        )}
                      </select>
                    )}
                    <div className="btn-group view-toggle">
                      <button type="button" aria-label="Open Table"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                      >
                        <FaTable />
                      </button>
                      <button type="button" aria-label="Open Grid"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <div className="text-center py-5">
                <p className="text-muted">Loading sections...</p>
              </div>
            ) : viewMode === 'table' ? (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`card shadow-sm border-0 ${styles.tableContainer}`}
              >
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className={`table ${styles.sectionTable}`}>
                      <thead>
                        <tr>
                          {isColumnEnabled(COLUMN_TYPES.SECTION_TITLE) && (
                          <th 
                            className="px-4 py-3" 
                            onClick={() => requestSort(SORT_KEYS.SECTION_TITLE)}
                          >
                            Title {getSortIcon(SORT_KEYS.SECTION_TITLE)}
                          </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.COURSE_NUMBER) && (
                          <th 
                            className="px-4 py-3" 
                            onClick={() => requestSort(SORT_KEYS.COURSE_NUMBER)}
                          >
                            Course Number {getSortIcon(SORT_KEYS.COURSE_NUMBER)}
                          </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.SCHEDULE_ID) && (
                          <th 
                            className="px-4 py-3" 
                            onClick={() => requestSort(SORT_KEYS.SCHEDULE_ID)}
                          >
                            Schedule ID {getSortIcon(SORT_KEYS.SCHEDULE_ID)}
                          </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.LOCATION) && (
                          <th 
                            className="px-4 py-3" 
                            onClick={() => requestSort(SORT_KEYS.LOCATION)}
                          >
                            Location {getSortIcon(SORT_KEYS.LOCATION)}
                          </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                          <th 
                            className="px-4 py-3" 
                            onClick={() => requestSort(SORT_KEYS.INSTRUCTOR)}
                          >
                            Instructor {getSortIcon(SORT_KEYS.INSTRUCTOR)}
                          </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((section, index) => (
                            <motion.tr 
                              key={index}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              {isColumnEnabled(COLUMN_TYPES.SECTION_TITLE) && (
                              <td className="px-4 py-3">
                                <p className={`${styles.sectionTitle} h6`}>{section.sectionTitle}</p>
                                <small className={styles.crn}>CRN: {section.crNstr}</small>
                              </td>
                              )}
                              {isColumnEnabled(COLUMN_TYPES.COURSE_NUMBER) && (
                              <td className="px-4 py-3">
                                <span className={styles.courseNumber}>
                                  {section.courseNumber}
                                </span>
                              </td>
                              )}
                              {isColumnEnabled(COLUMN_TYPES.SCHEDULE_ID) && (
                              <td className="px-4 py-3">
                                <span className={styles.scheduleId}>
                                  {section.scheduleId}
                                </span>
                              </td>
                              )}
                              {isColumnEnabled(COLUMN_TYPES.LOCATION) && (
                              <td className="px-4 py-3">
                                <span className={styles.locationText}>
                                  <FaMapMarkerAlt />
                                  {section.location}
                                </span>
                              </td>
                              )}
                              {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                              <td className="px-4 py-3">
                                <span className={styles.instructorText}>
                                  <FaUserTie />
                                  {section.instructorFullName}
                                </span>
                              </td>
                              )}
                            </motion.tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={Object.values(COLUMN_TYPES).filter(type => isColumnEnabled(type)).length || 1} className="text-center py-5">
                              <p className="text-muted mb-0">No sections found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="row g-4"
              >
                {currentItems.map((section, index) => (
                  <motion.div
                    key={index}
                    className="col-md-6 col-lg-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="card h-100 border-0 shadow-sm hover-shadow">
                      <div className="card-body">
                        {isColumnEnabled(COLUMN_TYPES.SECTION_TITLE) && (
                          <>
                        <h5 className="card-title mb-3">{section.sectionTitle}</h5>
                        <p className="text-muted mb-3">CRN: {section.crNstr}</p>
                          </>
                        )}
                        <div className="d-flex align-items-center mb-3">
                          {isColumnEnabled(COLUMN_TYPES.COURSE_NUMBER) && (
                          <span className="badge bg-light text-dark me-2">
                            {section.courseNumber}
                          </span>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.SCHEDULE_ID) && (
                          <span className="badge bg-primary">
                            {section.scheduleId}
                          </span>
                          )}
                        </div>
                        {isColumnEnabled(COLUMN_TYPES.LOCATION) && (
                        <div className="text-muted mb-2">
                          <FaMapMarkerAlt className="me-2" />
                          {section.location}
                        </div>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.INSTRUCTOR) && (
                        <div className="text-muted">
                          <FaUserTie className="me-2" />
                          {section.instructorFullName}
                        </div>
                        )}
                        <div className="text-muted mt-2">
                          <FaCalendarAlt className="me-2" />
                          {section.shortTermName}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {currentItems.length === 0 && (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted mb-0">No sections found</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalItems={getSortedSections(filteredSections).length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </motion.div>
   );
 };

export default Section;